/**
 * The parent's free chat with the agent (SPEC §6.5, `tutor-engine` §18).
 *
 * The dashboard answers *"est-ce que ça va ?"* in five seconds. It does not
 * answer *"pourquoi ?"* nor *"qu'est-ce que je fais ce week-end ?"* — this does.
 * It is reached **from a displayed fragility**, with the child already in
 * context, rather than living as a competing screen.
 *
 * Three interface constraints, all visible below:
 *
 * * The agent never serves verbatim, and this screen has no bubble that would
 *   expect one. There is no "ce qu'il a écrit" affordance to fill later.
 * * Any answer about a child is journalled, and the chat says so once, plainly,
 *   without apologising: the journal is readable by both parents, so it may as
 *   well be known.
 * * **The child is named in the question, never in the request.** The route
 *   carries no `child_id`; the agent names the child itself in each tool call,
 *   and that name is what gets journalled. Arriving from a child's page, the
 *   screen therefore prepends « Au sujet de <prénom> : » to the first question
 *   — *visibly*, in the bubble the parent sees, because a context added behind
 *   their back would make the journal record a read they did not ask for.
 *
 * Until 3 September 2026 this screen posted to `/api/parent/chat/messages`, an
 * endpoint that never existed: the whole surface was written, tested, and
 * unreachable on both sides at once.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import { Button, Card, Screen } from "@/ui/primitives";

type Turn = { author: "parent" | "agent"; text: string };

export function AgentChatScreen() {
  const { childId = "" } = useParams();
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [prenom, setPrenom] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const conversation = useRef<string | null>(null);

  useEffect(() => {
    if (!childId) return;
    let cancelled = false;
    void (async () => {
      try {
        // `listChildren` ne nomme personne dans son URL : la lire pour un
        // prénom ne journalise pas une consultation que le parent n'a pas
        // demandée. Le tableau de bord, lui, en écrirait une.
        const enfants = await call("listChildren");
        const trouve = enfants.find((enfant) => enfant.child_id === childId);
        if (!cancelled && trouve) setPrenom(trouve.display_name);
      } catch {
        /* sans prénom la question part telle quelle : le parent l'écrira */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [childId]);

  const contexte = useMemo(
    () => (prenom && turns.length === 0 ? `Au sujet de ${prenom} : ` : ""),
    [prenom, turns.length],
  );

  const ask = async () => {
    const saisie = draft.trim();
    if (!saisie || pending) return;
    const question = `${contexte}${saisie}`;
    setDraft("");
    setError(null);
    setPending(true);
    setTurns((previous) => [...previous, { author: "parent", text: question }]);
    try {
      const reponse = await call("poserAuChat", {
        body: {
          message: question,
          ...(conversation.current ? { conversation_id: conversation.current } : {}),
        },
      });
      conversation.current = reponse.conversation_id;
      setTurns((previous) => [...previous, { author: "agent", text: reponse.reponse }]);
    } catch (failure) {
      setError(
        failure instanceof ApiError
          ? failure.message
          : "La question n'est pas partie. Réessayez dans un instant.",
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Screen
      title="Poser une question"
      lead={prenom ? `Sur ${prenom}, à l'agent qui la suit.` : "À l'agent qui suit vos enfants."}
      actions={
        <Link className="btn btn--quiet" to={childId ? `/parents/enfant/${childId}` : "/parents"}>
          Retour
        </Link>
      }
    >
      <p className="notice">
        Chaque réponse portant sur votre enfant est inscrite au journal, lisible par les deux
        parents. L'agent ne restitue jamais ce que votre enfant a écrit.
      </p>

      <Card>
        <div className="chat chat--parent">
          {turns.map((turn, index) => (
            <article
              key={index}
              className={`bubble bubble--${turn.author === "parent" ? "child" : "tutor"}`}
            >
              <p>{turn.text}</p>
            </article>
          ))}
          {pending ? <p className="muted">L'agent cherche…</p> : null}
        </div>

        {error ? <p className="notice notice--attention">{error}</p> : null}

        <form
          className="composer"
          onSubmit={(event) => {
            event.preventDefault();
            void ask();
          }}
        >
          <label className="sr-only" htmlFor="agent-question">
            Votre question
          </label>
          {contexte ? <p className="muted">La question partira avec « {contexte.trim()} »</p> : null}
          <textarea
            id="agent-question"
            className="composer__input"
            rows={2}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Pourquoi les fractions coincent encore ?"
          />
          <Button type="submit" disabled={!draft.trim() || pending}>
            Demander
          </Button>
        </form>
      </Card>
    </Screen>
  );
}
